/** biome-ignore-all lint/suspicious/noConsole: App startup logging: the app version and commit hash */
// The general stylesheets must be imported before the ./App to ensure correct stylesheets order

import '@schooly/style/src/css/custom_bootstrap.scss';
import '@schooly/style/src/css/index.scss';

import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { initializeSentry } from '@schooly/sentry';

import moment from 'moment';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Amplify, Auth } from 'aws-amplify';
import { init } from '@schooly/api';

import packageInfo from '@schooly/package-info';
import App from './App';
import {
  AWS_CONFIG,
  BUILD_DATE,
  DEBUG,
  COMMIT_SHA,
  LANGUAGE,
  SERVER,
  SENTRY_DSN,
  IS_DEV,
  SENTRY_ENVIRONMENT_NAME,
  SENTRY_TRACES_SAMPLE_RATE,
  SENTRY_PROFILES_SAMPLE_RATE,
  AMPLITUDE_API_KEY,
} from './config';
import { WithMaintenance } from './context/maintenance/WithMaintenance';
import { store } from './redux/store';
import * as serviceWorker from './serviceWorker';
import { IntlProviderWithMessages } from '@schooly/utils/translations';
import { queryClient, persistOptions, clearReactQueryStorageCache } from './queryClient';
import { initDateDefaultOptions } from '@schooly/utils/date';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { DEFAULT_SESSION_REPLAY_SAMPLE_RATE } from '@schooly/analytics';
init({
  baseURL: SERVER,
  verbose: DEBUG,
  onUnauthenticated: async () => {
    await Auth.signOut();
    clearReactQueryStorageCache();
    window.location.href = '/';
  },
});

// AWS cognito and S3 configuration
Amplify.configure(AWS_CONFIG);

declare global {
  interface Window {
    INIT_URL: URL;
  }
}

window.INIT_URL = new URL(window.location.href);

console.log('');
console.group('Schooly Web App');
console.log(
  `App version: ${packageInfo.version}${
    BUILD_DATE ? ` (built at ${moment(BUILD_DATE).format()})` : ''
  }`,
);
if (COMMIT_SHA) {
  console.log('Latest commit from the build:');
  console.log(`https://github.com/myTreehive/treehive-web/commit/${COMMIT_SHA}`);
}
console.groupEnd();
console.log('');

if (!IS_DEV) {
  const sessionReplayTracking = sessionReplayPlugin({
    sampleRate: DEFAULT_SESSION_REPLAY_SAMPLE_RATE,
  });
  amplitude.add(sessionReplayTracking);
  amplitude.init(AMPLITUDE_API_KEY, { fetchRemoteConfig: true, autocapture: true });

  initializeSentry({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT_NAME,
    targets: [SERVER],
    debug: DEBUG,
    releaseVersion: packageInfo.version,
    tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
    profilesSampleRate: SENTRY_PROFILES_SAMPLE_RATE,
  });
}

initDateDefaultOptions(LANGUAGE);

ReactDOM.render(
  <React.StrictMode>
    <PersistQueryClientProvider persistOptions={persistOptions} client={queryClient}>
      <Provider store={store}>
        <IntlProviderWithMessages overrideLanguage={LANGUAGE}>
          <BrowserRouter>
            <WithMaintenance>
              <App />
            </WithMaintenance>
          </BrowserRouter>
        </IntlProviderWithMessages>
      </Provider>
    </PersistQueryClientProvider>
  </React.StrictMode>,
  document.getElementById('root'),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
